import React, { useState } from "react";
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  SafeAreaView,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useAuth } from "../../contexts/AuthContext";
import { useTheme } from "../../contexts/ThemeContext";
import Header from "../../components/Header";
import { useNavigation } from "@react-navigation/native";

const LoginScreenPage: React.FC = () => {
  const { theme } = useTheme();
  const navigation = useNavigation<any>();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const { login, isLoading } = useAuth();

  // Theme-aware colors
  const themed = {
    background: theme === "dark" ? "#18181B" : "#FAFAFA",
    title: theme === "dark" ? "#FAFAFA" : "#2D3748",
    subtitle: theme === "dark" ? "#A1A1AA" : "#718096",
    inputBg: theme === "dark" ? "#27272A" : "#F7FAFC",
    inputBorder: theme === "dark" ? "#3A3A3D" : "#E2E8F0",
    inputText: theme === "dark" ? "#FAFAFA" : "#2D3748",
    iconColor: theme === "dark" ? "#A1A1AA" : "#718096",
    placeholderColor: theme === "dark" ? "#71717A" : "#718096",
    buttonBg: theme === "dark" ? "#FAFAFA" : "#272727",
    buttonText: theme === "dark" ? "#18181B" : "#FFFFFF",
    linkText: theme === "dark" ? "#93C5FD" : "#3B82F6",
    backText: theme === "dark" ? "#A1A1AA" : "#718096",
    errorBg: theme === "dark" ? "#431316" : "#FFF5F5",
    errorBorder: theme === "dark" ? "#7F1D1D" : "#FED7D7",
    errorText: theme === "dark" ? "#FCA5A5" : "#E53E3E",
  };

  const handleLogin = async () => {
    // Basic validation
    if (!username.trim() || !password.trim()) {
      setErrorMessage("Please enter both username and password");
      return;
    }

    try {
      setErrorMessage("");
      await login(username, password);
      navigation.navigate("Home"); // Navigate back to home after successful login
    } catch (error: any) {
      console.error("Login error:", error);
      setErrorMessage(
        error.message || "Failed to login. Please check your credentials.",
      );
    }
  };

  return (
    <SafeAreaView
      style={[styles.safeArea, { backgroundColor: themed.background }]}
      data-oid="zcy-y80"
    >
      <Header data-oid="s2wo5_b" />

      <KeyboardAvoidingView
        behavior={Platform.OS === "ios" ? "padding" : "height"}
        style={[styles.container, { backgroundColor: themed.background }]}
        data-oid="p5cel90"
      >
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          data-oid="6i9q2z."
        >
          <View style={styles.header} data-oid="okenxzg">
            <Text
              style={[styles.title, { color: themed.title }]}
              data-oid="bs4qmkq"
            >
              Welcome Back
            </Text>
            <Text
              style={[styles.subtitle, { color: themed.subtitle }]}
              data-oid="zm:oufa"
            >
              Log in to save opportunities and more
            </Text>
          </View>

          <View style={styles.formContainer} data-oid="da9fjzz">
            {errorMessage ? (
              <View
                style={[
                  styles.errorContainer,
                  {
                    backgroundColor: themed.errorBg,
                    borderColor: themed.errorBorder,
                  },
                ]}
                data-oid="o-ocd.z"
              >
                <Ionicons
                  name="alert-circle-outline"
                  size={20}
                  color={themed.errorText}
                  data-oid="w8-xcvd"
                />

                <Text
                  style={[styles.errorText, { color: themed.errorText }]}
                  data-oid="u_kc-l6"
                >
                  {errorMessage}
                </Text>
              </View>
            ) : null}

            <View
              style={[
                styles.inputContainer,
                {
                  backgroundColor: themed.inputBg,
                  borderColor: themed.inputBorder,
                },
              ]}
              data-oid="0j.7dqs"
            >
              <Ionicons
                name="person-outline"
                size={20}
                color={themed.iconColor}
                style={styles.inputIcon}
                data-oid="j01z1_2"
              />

              <TextInput
                style={[styles.input, { color: themed.inputText }]}
                placeholder="Username"
                value={username}
                onChangeText={setUsername}
                autoCapitalize="none"
                autoCorrect={false}
                placeholderTextColor={themed.placeholderColor}
                data-oid="mf8r2tz"
              />
            </View>

            <View
              style={[
                styles.inputContainer,
                {
                  backgroundColor: themed.inputBg,
                  borderColor: themed.inputBorder,
                },
              ]}
              data-oid="o6ybaif"
            >
              <Ionicons
                name="lock-closed-outline"
                size={20}
                color={themed.iconColor}
                style={styles.inputIcon}
                data-oid="yakhxgn"
              />

              <TextInput
                style={[styles.input, { color: themed.inputText }]}
                placeholder="Password"
                value={password}
                onChangeText={setPassword}
                secureTextEntry
                placeholderTextColor={themed.placeholderColor}
                data-oid="ihoccc4"
              />
            </View>

            <TouchableOpacity
              style={[styles.loginButton, { backgroundColor: themed.buttonBg }]}
              onPress={handleLogin}
              disabled={isLoading}
              data-oid="ni1ddw4"
            >
              {isLoading ? (
                <ActivityIndicator
                  color={themed.buttonText}
                  size="small"
                  data-oid="pjdu:p4"
                />
              ) : (
                <Text
                  style={[styles.loginButtonText, { color: themed.buttonText }]}
                  data-oid=".n4t3wk"
                >
                  Log In
                </Text>
              )}
            </TouchableOpacity>

            <View style={styles.signupContainer} data-oid="aykb6d1">
              <Text
                style={[styles.signupText, { color: themed.subtitle }]}
                data-oid=".0ske0i"
              >
                Don't have an account?
              </Text>
              <TouchableOpacity
                onPress={() => navigation.navigate("SignUp")}
                data-oid="8tbtk0f"
              >
                <Text
                  style={[styles.signupLink, { color: themed.linkText }]}
                  data-oid="59p3gs."
                >
                  Sign Up
                </Text>
              </TouchableOpacity>
            </View>

            <TouchableOpacity
              style={styles.backButton}
              onPress={() => navigation.navigate("Home")}
              data-oid="03z6owy"
            >
              <Ionicons
                name="arrow-back"
                size={18}
                color={themed.backText}
                data-oid="2044r8d"
              />

              <Text
                style={[styles.backButtonText, { color: themed.backText }]}
                data-oid=":d0difo"
              >
                Back to Opportunities
              </Text>
            </TouchableOpacity>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: "#FAFAFA",
  },
  container: {
    flex: 1,
    backgroundColor: "#FAFAFA",
  },
  scrollContent: {
    flexGrow: 1,
    padding: 24,
    paddingTop: 20,
    maxWidth: 600,
    width: "100%",
    alignSelf: "center",
  },
  header: {
    marginBottom: 40,
    alignItems: "center",
  },
  title: {
    fontSize: 28,
    fontWeight: "bold",
    color: "#2D3748",
    marginBottom: 8,
    fontFamily: "Inter-Bold",
  },
  subtitle: {
    fontSize: 16,
    color: "#718096",
    fontFamily: "Inter-Regular",
    textAlign: "center",
  },
  formContainer: {
    width: "100%",
    maxWidth: 400,
    alignSelf: "center",
  },
  errorContainer: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#FFF5F5",
    padding: 16,
    borderRadius: 12,
    marginBottom: 20,
    borderWidth: 1,
    borderColor: "#FED7D7",
  },
  errorText: {
    color: "#E53E3E",
    marginLeft: 8,
    fontSize: 14,
    fontFamily: "Inter-Medium",
    flex: 1,
  },
  inputContainer: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#F7FAFC",
    borderRadius: 12,
    paddingHorizontal: 16,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: "#E2E8F0",
  },
  inputIcon: {
    marginRight: 12,
  },
  input: {
    flex: 1,
    height: 50,
    fontSize: 16,
    color: "#2D3748",
    fontFamily: "Inter-Regular",
  },
  loginButton: {
    backgroundColor: "#272727",
    borderRadius: 12,
    height: 50,
    alignItems: "center",
    justifyContent: "center",
    marginTop: 10,
  },
  loginButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
    fontFamily: "Inter-SemiBold",
  },
  signupContainer: {
    flexDirection: "row",
    justifyContent: "center",
    marginTop: 24,
    alignItems: "center",
  },
  signupText: {
    color: "#718096",
    fontSize: 14,
    fontFamily: "Inter-Regular",
  },
  signupLink: {
    color: "#3B82F6",
    fontSize: 14,
    fontWeight: "600",
    marginLeft: 4,
    fontFamily: "Inter-SemiBold",
  },
  backButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    marginTop: 40,
    paddingVertical: 12,
  },
  backButtonText: {
    color: "#718096",
    fontSize: 14,
    marginLeft: 6,
    fontFamily: "Inter-Medium",
  },
});

export default LoginScreenPage;
